import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatMenuTrigger} from '@angular/material/menu';
import {LoadingService, LocalConfigUtils, ManagedSubscriber, UserService} from '@nh/angular-tses';
import {AccessibilityKeysService, DasAccessibilityItem} from 'app/Accessibility/accessibility-keys.service';
import {GlobalAction} from '../../permissions/types';
import {ConfirmDialogComponent} from './confirm-dialog/confirm-dialog.component';
import {FeedbackDialogComponent} from './feedback-dialog/feedback-dialog.component';
import {HeaderService} from './header.service';
import {EnvironmentVariableConfig, EnvironmentVariableService} from '../services/environment-variable.service';

@Component({
  selector: 'das-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent extends ManagedSubscriber implements OnInit, OnDestroy {
  @Output() accessibilityToggle: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('userMenuTrigger')
  public userMenuTrigger: MatMenuTrigger;

  public toolbarVisible: boolean;
  public GlobalAction: typeof GlobalAction = GlobalAction;
  public loadingBarVisible: boolean;
  public headerBackgroundColour: string;

  public links = [
    {
      path: '/departures',
      label: 'Home',
      icon: 'home',
      altProperties: this.accessibilityKeysService.getAltProperties(DasAccessibilityItem.HEADER_HOME),
    },
    {
      path: '/search',
      label: 'Search',
      icon: 'search',
      altProperties: this.accessibilityKeysService.getAltProperties(DasAccessibilityItem.HEADER_SEARCH),
    },
  ];

  public userFullName: string;
  public userFirstName: string;

  public offline: boolean = LocalConfigUtils.getConfig().offline;

  public readonly DasAccessibilityItem: typeof DasAccessibilityItem = DasAccessibilityItem;

  @Input() accessibilityToggleChecked: boolean;

  constructor(
    private _environmentVariableService: EnvironmentVariableService,
    private headerService: HeaderService,
    private userService: UserService,
    public accessibilityKeysService: AccessibilityKeysService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.headerService.onToolbarVisibilityChange((toolbarVisible) => (this.toolbarVisible = toolbarVisible)),
      LoadingService.onLoadingStateChange((loading: boolean) => (this.loadingBarVisible = loading)),
      this._environmentVariableService.onConfigChange(
        (config: EnvironmentVariableConfig) => (this.headerBackgroundColour = config.headerBackgroundColour)
      )
    );
    this.subscribeTo(this.userService.getUser(), (user) => {
      this.userFirstName = user ? user.firstName : null;
      this.userFullName = user ? user.name : null;
    });

    this.headerBackgroundColour = this._environmentVariableService.getConfig().headerBackgroundColour;
  }

  public create(): void {
    this.dialog.open(ConfirmDialogComponent, {
      ariaLabel: 'confirmation dialog',
      disableClose: true,
      width: '50vw',
    });
  }

  public feedback(): void {
    this.dialog.open(FeedbackDialogComponent, {
      ariaLabel: 'Feedback dialog',
      disableClose: true,
      width: '50vw',
    });
  }

  public userManagementUrl(): string {
    return this.userService.userManagement();
  }

  public accountManagementUrl(): string {
    return this.userService.createAccountUrl();
  }

  public logoutUrl(): string {
    return this.userService.logoutUrl();
  }

  public forceLogout(): void {
    localStorage.removeItem('kc-token');
    localStorage.removeItem('kc-refresh-token');
  }

  public userLocationsUrl(): string {
    return 'locations';
  }

  /**
   * Handles the case when the alt event for the logo, CARS Wiki or Contact
   * Support buttons are triggered.
   *
   * @param id {string} The id of the document elements to be clicked.
   */
  public handleAltSelection(id: string): void {
    document.getElementById(id).click();
  }

  public openUserMenu() {
    this.userMenuTrigger.openMenu();
    this.cd.markForCheck();
  }

  public onKeyboardEnter() {
    this.accessibilityToggleChecked = !this.accessibilityToggleChecked;
    this.accessibilityToggle.next(this.accessibilityToggleChecked);
    localStorage.setItem('show-accessibility-focus', this.accessibilityToggleChecked.toString());
  }

  public onToggle(event) {
    this.accessibilityToggle.next(event.checked);
    localStorage.setItem('show-accessibility-focus', event.checked.toString());
  }
}
